import type { ComponentProps, HTMLAttributes, ReactElement, ReactNode } from "react";
import {
  Children,
  createContext,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { ChevronLeftIcon, ChevronRightIcon, PaperclipIcon, XIcon } from "lucide-react";
import { cjk } from "@streamdown/cjk";
import { code } from "@streamdown/code";
import { math } from "@streamdown/math";
import { mermaid } from "@streamdown/mermaid";
import { Streamdown } from "streamdown";

import "streamdown/styles.css";

import { cn } from "./lib/cn";
import type { FileUIPart, UIMessage } from "./types";
import { Button } from "./ui/button";
import { ButtonGroup, ButtonGroupText } from "./ui/button-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

export type MessageProps = HTMLAttributes<HTMLDivElement> & { from: UIMessage["role"] };
/** Groups a chat message and applies sender-specific layout. */
export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      "group flex w-full max-w-[95%] flex-col gap-2",
      from === "user" ? "is-user ml-auto justify-end" : "is-assistant",
      className,
    )}
    {...props}
  />
);
/** Bubble container for chat message content. */
export const MessageContent = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "is-user:dark flex w-fit max-w-full min-w-0 flex-col gap-2 overflow-hidden text-sm",
      "group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground",
      "group-[.is-assistant]:text-foreground",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);
/** Layout container for controls associated with a chat message. */
export const MessageActions = ({ className, ...props }: ComponentProps<"div">) => (
  <div className={cn("flex items-center gap-1", className)} {...props} />
);
/** Button action with optional accessible tooltip. */
export const MessageAction = ({
  tooltip,
  label,
  children,
  variant = "ghost",
  size = "icon-sm",
  ...props
}: ComponentProps<typeof Button> & { tooltip?: string; label?: string }) => {
  const button = (
    <Button size={size} type="button" variant={variant} {...props}>
      {children}
      <span className="sr-only">{label || tooltip}</span>
    </Button>
  );
  return tooltip ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    button
  );
};

export type MessageResponseProps = ComponentProps<typeof Streamdown>;

const streamdownPlugins = { cjk, code, math, mermaid };

/** Keeps Streamdown's word animation sequence continuous across Markdown block boundaries. */
const parseAnimatedMarkdownAsSingleBlock = (markdown: string) => [markdown];

/** Renders assistant Markdown with Vercel AI Elements' Streamdown configuration. */
export const MessageResponse = memo(
  ({ animated, className, ...props }: MessageResponseProps) => (
    <Streamdown
      animated={animated}
      className={cn("size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0", className)}
      parseMarkdownIntoBlocksFn={animated ? parseAnimatedMarkdownAsSingleBlock : undefined}
      plugins={streamdownPlugins}
      {...props}
    />
  ),
  (previous, next) =>
    previous.children === next.children && next.isAnimating === previous.isAnimating,
);
MessageResponse.displayName = "MessageResponse";

/** Classifies an attachment for the appropriate message presentation. */
export const getMediaCategory = (data: FileUIPart) =>
  data.mediaType?.startsWith("image/") && data.url ? "image" : "file";
/** Returns the visible attachment label used by message attachment UI. */
export const getAttachmentLabel = (data: FileUIPart) =>
  data.filename || (getMediaCategory(data) === "image" ? "Image" : "Attachment");
export type MessageAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: FileUIPart;
  onRemove?: () => void;
};
/** Displays an image or file attachment and optional removal affordance. */
export const MessageAttachment = ({
  data,
  className,
  onRemove,
  ...props
}: MessageAttachmentProps) => {
  const isImage = getMediaCategory(data) === "image";
  const label = getAttachmentLabel(data);
  return (
    <div className={cn("group relative size-24 overflow-hidden rounded-lg", className)} {...props}>
      {isImage ? (
        <img
          alt={data.filename || "attachment"}
          className="size-full object-cover"
          src={data.url}
        />
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex size-full items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <PaperclipIcon className="size-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {onRemove && (
        <Button
          aria-label="Remove attachment"
          className="absolute top-2 right-2 size-6 rounded-full p-0 opacity-0 group-hover:opacity-100"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          type="button"
          variant="ghost"
        >
          <XIcon />
          <span className="sr-only">Remove</span>
        </Button>
      )}
    </div>
  );
};
/** Renders message attachments only when they are present. */
export const MessageAttachments = ({ children, className, ...props }: ComponentProps<"div">) =>
  children ? (
    <div className={cn("ml-auto flex w-fit flex-wrap items-start gap-2", className)} {...props}>
      {children}
    </div>
  ) : null;
/** Two-column attachment layout. */
export const MessageAttachmentGrid = (props: ComponentProps<"div">) => (
  <MessageAttachments className="grid grid-cols-2" {...props} />
);
/** Single-row attachment layout. */
export const MessageAttachmentInline = (props: ComponentProps<"div">) => (
  <MessageAttachments className="flex-nowrap" {...props} />
);
/** Vertical attachment layout. */
export const MessageAttachmentList = (props: ComponentProps<"div">) => (
  <MessageAttachments className="w-full flex-col" {...props} />
);
/** Layout container for message-level actions. */
export const MessageToolbar = ({ className, ...props }: ComponentProps<"div">) => (
  <div
    className={cn("mt-4 flex w-full items-center justify-between gap-4", className)}
    {...props}
  />
);

type MessageBranchContextValue = {
  currentBranch: number;
  totalBranches: number;
  goToPrevious: () => void;
  goToNext: () => void;
};

const MessageBranchContext = createContext<MessageBranchContextValue | null>(null);

/** Returns navigation state for message branches. */
export const useMessageBranch = () => {
  const context = useContext(MessageBranchContext);
  if (!context) {
    throw new Error("Message branch components must be used within MessageBranch");
  }
  return context;
};

export type MessageBranchProps = HTMLAttributes<HTMLDivElement> & {
  defaultBranch?: number;
  onBranchChange?: (branchIndex: number) => void;
};

/** Coordinates a set of alternative message branches and their navigation controls. */
export const MessageBranch = ({
  children,
  className,
  defaultBranch = 0,
  onBranchChange,
  ...props
}: MessageBranchProps) => {
  const [currentBranch, setCurrentBranch] = useState(defaultBranch);
  const branchContent = Children.toArray(children).find(
    (child): child is ReactElement<MessageBranchContentProps> =>
      isValidElement(child) && child.type === MessageBranchContent,
  );
  const totalBranches = branchContent ? Children.toArray(branchContent.props.children).length : 0;
  const selectedBranch = Math.max(0, Math.min(currentBranch, totalBranches - 1));
  const selectBranch = useCallback(
    (branchIndex: number) => {
      if (totalBranches === 0) {
        return;
      }
      const nextBranch = Math.max(0, Math.min(branchIndex, totalBranches - 1));
      setCurrentBranch(nextBranch);
      onBranchChange?.(nextBranch);
    },
    [onBranchChange, totalBranches],
  );
  const goToPrevious = useCallback(() => {
    if (totalBranches > 0) {
      selectBranch((selectedBranch - 1 + totalBranches) % totalBranches);
    }
  }, [selectBranch, selectedBranch, totalBranches]);
  const goToNext = useCallback(() => {
    if (totalBranches > 0) {
      selectBranch((selectedBranch + 1) % totalBranches);
    }
  }, [selectBranch, selectedBranch, totalBranches]);
  const value = useMemo(
    () => ({ currentBranch: selectedBranch, goToNext, goToPrevious, totalBranches }),
    [goToNext, goToPrevious, selectedBranch, totalBranches],
  );

  return (
    <MessageBranchContext.Provider value={value}>
      <div className={cn("grid w-full gap-2 [&>div]:pb-0", className)} {...props}>
        {children}
      </div>
    </MessageBranchContext.Provider>
  );
};

export type MessageBranchContentProps = HTMLAttributes<HTMLDivElement>;

/** Renders only the selected child of a {@link MessageBranch}. */
export const MessageBranchContent = ({
  children,
  className,
  ...props
}: MessageBranchContentProps) => {
  const { currentBranch } = useMessageBranch();
  return Children.toArray(children).map((branch, index) => (
    <div
      className={cn(
        "grid gap-2 overflow-hidden [&>div]:pb-0",
        index === currentBranch ? "block" : "hidden",
        className,
      )}
      key={isValidElement(branch) && branch.key !== null ? branch.key : index}
      {...props}
    >
      {branch}
    </div>
  ));
};

export type MessageBranchSelectorProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};

// The source API exposes `from`; honor it at this message boundary so controls follow the existing
// sender alignment contract instead of relying on an unused upstream prop.
/** Groups branch navigation controls, aligning user controls right and non-user controls left. */
export const MessageBranchSelector = ({
  className,
  from,
  ...props
}: MessageBranchSelectorProps) => {
  const { totalBranches } = useMessageBranch();
  if (totalBranches <= 1) {
    return null;
  }
  return (
    <ButtonGroup
      className={cn(
        "[&>*:not(:first-child)]:rounded-l-md [&>*:not(:last-child)]:rounded-r-md",
        from === "user" ? "ml-auto" : "mr-auto",
        className,
      )}
      {...props}
    />
  );
};

// Local adaptation: this shared package has no product i18n context, so callers must supply the
// localized accessible label instead of adopting upstream's fixed English branch-control labels.
export type MessageBranchControlProps = Omit<ComponentProps<typeof Button>, "aria-label"> & {
  ariaLabel: string;
};

/** Props for a previous-branch control with a caller-provided localized accessible label. */
export type MessageBranchPreviousProps = MessageBranchControlProps;

/** Moves a {@link MessageBranch} selection to its previous branch. */
export const MessageBranchPrevious = ({
  ariaLabel,
  children,
  className,
  ...props
}: MessageBranchPreviousProps) => {
  const { goToPrevious, totalBranches } = useMessageBranch();
  return (
    <Button
      aria-label={ariaLabel}
      className={className}
      disabled={totalBranches <= 1}
      onClick={goToPrevious}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <ChevronLeftIcon className="size-4" />}
    </Button>
  );
};

export type MessageBranchPageProps = Omit<ComponentProps<typeof ButtonGroupText>, "children"> & {
  children: (currentBranch: number, totalBranches: number) => ReactNode;
};

/** Displays caller-localized selected-branch and total-branch content. */
export const MessageBranchPage = ({ className, children, ...props }: MessageBranchPageProps) => {
  const { currentBranch, totalBranches } = useMessageBranch();
  return (
    <ButtonGroupText className={className} {...props}>
      {children(currentBranch, totalBranches)}
    </ButtonGroupText>
  );
};

/** Props for a next-branch control with a caller-provided localized accessible label. */
export type MessageBranchNextProps = MessageBranchControlProps;

/** Moves a {@link MessageBranch} selection to its next branch. */
export const MessageBranchNext = ({
  ariaLabel,
  children,
  className,
  ...props
}: MessageBranchNextProps) => {
  const { goToNext, totalBranches } = useMessageBranch();
  return (
    <Button
      aria-label={ariaLabel}
      className={className}
      disabled={totalBranches <= 1}
      onClick={goToNext}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <ChevronRightIcon className="size-4" />}
    </Button>
  );
};

export type MessagePart = ReactNode;
